import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { getInvoicePDF } from "@/redux/resources/resources.action";
import { DownloadData } from "@/redux/resources/types/Resources.interface";
import { formattedNumber } from "@/utilities/formatNumbers";
import { ErrorToast } from "@/utilities/toasts/ErrorToast";
import { SuccessToast } from "@/utilities/toasts/SuccessToast";
import { Typography } from "@mui/material";
import { ColumnDef, Row } from "@tanstack/react-table";
import { Download, LoaderCircle } from "lucide-react";
import { useState } from "react";
import { useLocation } from "react-router-dom";

export type InvoicingDataColumns = {
  tipo: string;
  numero: string;
  fecha: string;
  total: string;
  moneda: string;
  serie: string;
};

const monedaLabels = {
  1: "Pesos",
  2: "USD",
};
const typeInvoicing = {
  111: "Factura",
};

export function useInvoicingDataColumns(): ColumnDef<InvoicingDataColumns>[] {
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const { state } = useLocation();
  const { folderId } = state || {};
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const handleDownloadPDF = async (data: InvoicingDataColumns) => {
    const invoiceData: DownloadData = {
      FolderNro: folderId,
      InvoiceNro: data.numero,
      InvoiceSerie: data.serie,
      InvoiceType: data.tipo,
    };
    setLoadingId(data.numero);
    try {
      const result = await dispatch(getInvoicePDF(invoiceData));
      if (result.payload) {
        window.open(result.payload as string, "_blank");
        SuccessToast({ message: "El archivo se ha descargado correctamente" });
      } else {
        ErrorToast({
          message:
            "No se ha podido descargar el archivo, favor de contactar a un administrador",
        });
      }
    } catch (error) {
      ErrorToast({
        message:
          "No se ha podido descargar el archivo, favor de contactar a un administrador",
      });
    } finally {
      setLoadingId(null);
    }
  };
  const columnsInvoicingData: ColumnDef<InvoicingDataColumns>[] = [
    {
      accessorKey: "tipo",
      header: () => (
        <div className="text-left">
          <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
            Tipo
          </Typography>
        </div>
      ),
      cell: ({ row }: { row: Row<InvoicingDataColumns> }) => {
        return (
          <div className="text-left font-medium" aria-label="tipo">
            <Typography
              fontFamily={"Inter"}
              fontSize={"14px"}
              fontWeight={400}
              className="truncate"
            >
              {typeInvoicing[
                Number(row.original.tipo) as keyof typeof typeInvoicing
              ] || "- - -"}
            </Typography>
          </div>
        );
      },
    },
    {
      accessorKey: "numero",
      header: () => (
        <div className="text-left max-w-[75px]">
          <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
            {isMobile ? "Nro. factura" : "Número de factura"}
          </Typography>
        </div>
      ),
      cell: ({ row }: { row: Row<InvoicingDataColumns> }) => {
        return (
          <div className="text-left font-medium" aria-label="numero">
            <Typography
              fontFamily={"Inter"}
              fontSize={"14px"}
              fontWeight={400}
              className="truncate max-w-[75px]"
            >
              {row.original.numero || "Sin Nro. factura"}
            </Typography>
          </div>
        );
      },
      enableHiding: false,
    },
    {
      accessorKey: "fecha",
      header: () => (
        <div className={`text-left ${isMobile ? "pl-2" : ""}`}>
          <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
            Fecha
          </Typography>
        </div>
      ),
      cell: ({ row }: { row: Row<InvoicingDataColumns> }) => {
        return (
          <div className="text-left font-medium" aria-label="fecha">
            <Typography
              fontFamily={"Inter"}
              fontSize={"14px"}
              fontWeight={400}
              className="truncate"
            >
              {row.original.fecha || "Sin fecha"}
            </Typography>
          </div>
        );
      },
    },
    {
      accessorKey: "moneda",
      header: () => (
        <div className={`text-left ${isMobile ? "pl-2" : ""}`}>
          <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
            Moneda
          </Typography>
        </div>
      ),
      cell: ({ row }: { row: Row<InvoicingDataColumns> }) => {
        return (
          <div className="text-left font-medium">
            <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={400}>
              {
                monedaLabels[
                  Number(row.original.moneda) as keyof typeof monedaLabels
                ]
              }
            </Typography>
          </div>
        );
      },
    },
    {
      accessorKey: "total",
      header: () => (
        <div className={`text-left ${isMobile ? "pl-2" : ""}`}>
          <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
            Total
          </Typography>
        </div>
      ),
      cell: ({ row }: { row: Row<InvoicingDataColumns> }) => {
        return (
          <div className="text-left font-medium" aria-label="total">
            <Typography
              fontFamily={"Inter"}
              fontSize={"14px"}
              fontWeight={400}
              className="truncate"
            >
              {`${formattedNumber(row.original.total)}`}
            </Typography>
          </div>
        );
      },
    },
    {
      id: "actions",
      cell: ({ row }: { row: Row<InvoicingDataColumns> }) => {
        const isRowLoading = loadingId === row.original.numero;
        return (
          <div className="text-left font-medium" aria-label="actions">
            {!isRowLoading && (
              <Button
                variant="icon"
                size="icon"
                onClick={() => handleDownloadPDF(row.original)}
              >
                <Download />
              </Button>
            )}
            {isRowLoading && (
              <div className="flex items-center justify-center">
                <LoaderCircle className="h-6 w-6 animate-spin text-gray-500" />
              </div>
            )}
          </div>
        );
      },
    },
  ].filter(Boolean) as ColumnDef<InvoicingDataColumns>[];
  return columnsInvoicingData;
}
